import React from 'react';
import { useAuth } from "./context/AuthContext";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useEffect } from 'react';

// Pages
import Landing from './pages/Landing';
import About from './pages/About';
import Contact from './pages/Contact';
import Pricing from './pages/Pricing';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Login from './pages/Login';
import Register from './pages/Register';
import Jobs from './pages/Jobs';
import JobDetails from './pages/JobDetails';
import CandidateDashboard from './pages/CandidateDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminUsers from './pages/SuperAdminUsers';
import SuperAdminAdmins from './pages/SuperAdminAdmins';
import SuperAdminJobs from './pages/SuperAdminJobs';
import SuperAdminInterviews from './pages/SuperAdminInterviews';
import SuperAdminTickets from './pages/SuperAdminTickets';
import SuperAdminSubscriptions from './pages/SuperAdminSubscriptions';
import SuperAdminAnalytics from './pages/SuperAdminAnalytics';
import CustomPricingDashboard from './pages/CustomPricingDashboard';
import MyJobs from './pages/MyJobs';
import RecruiterRequests from './pages/RecruiterRequests';
import PostJob from './pages/PostJob';
import CodingTest from './pages/CodingTest';
import AIInterview from './pages/AIInterview';
import TechnicalInterview from './pages/TechnicalInterview';
import HRInterview from './pages/HRInterview';
// import AITechnicalInterview from './pages/AITechnicalInterview';
import Profile from './pages/Profile';
import Subscription from './pages/Subscription';
import Payment from './pages/Payment';
import Support from './pages/Support';
import InterviewHistory from './pages/InterviewHistory';
import AdminRequest from './pages/AdminRequest';

import JobApplications from './pages/JobApplications';
import MyApplications from './pages/MyApplications';
import SavedJobs from './pages/SavedJobs';
import ApplicationDetails from './pages/ApplicationDetails';
import JobInterviewHistory from './pages/JobInterviewHistory';
import OfferLetterTemplates from './pages/OfferLetterTemplates';
import Technologies from './pages/Technologies';
import ReleaseOfferLetter from './pages/ReleaseOfferLetter';
import InterviewPanel from './pages/InterviewPanel';
import NotFound from './pages/NotFound';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmailOtp from './pages/VerifyEmailOtp';
import CandidateScreening from './pages/CandidateScreening';
import FreeTrialConfirm from './pages/FreeTrialConfirm';
import Analytics from './pages/Analytics';
import CandidateComparison from './pages/CandidateComparison';
import PublicJobPage from './pages/PublicJobPage';
import InterviewStatus from './pages/InterviewStatus';
import DirectInterviews from './pages/DirectInterviews';
import InterviewRecordings from './pages/InterviewRecordings';
import PaymentSuccess from './pages/PaymentSuccess';
import CommunicationOverview from './pages/CommunicationOverview';
import CommunicationTemplates from './pages/CommunicationTemplates';
import CommunicationHistory from './pages/CommunicationHistory';
import CreditLedger from './pages/CreditLedger';
import CreditUsage from './pages/CreditUsage';
import PurchaseHistory from './pages/PurchaseHistory';
import AICalling from './pages/AICalling';
import BookDemo from './pages/BookDemo';
import ScheduledDemos from './pages/superadmin/ScheduledDemos';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Sidebar from './components/Sidebar';
import PrivateRoute from './components/PrivateRoute';
import { Box, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import UserPricing from './pages/UserPricing';
import GlobalSecurityPolicy from './components/GlobalSecurityPolicy';

// System Quality Check — new isolated pre-interview gate (does not modify AIInterview/PrivateRoute)
import SystemCheckGate from './features/systemQualityCheck/SystemCheckGate';

function App() {
    return (
        <AuthProvider>
            <GlobalSecurityPolicy />
            <AppContent />
        </AuthProvider>
    );
}

function AppContent() {
    const sidebarRoutes = [
        '/candidate/dashboard',
        '/admin/dashboard',
        '/admin/jobs',
        '/super-admin/dashboard',
        '/profile',
        '/subscription',
        '/payment',
        '/support',
        '/interview-history',
        '/admin-request',
        '/super-admin/payments-history',
        '/super-admin/admin-history',
        '/super-admin/user-history',
        '/super-admin/interview-history',
        '/super-admin/jobs-history',
        '/super-admin/recruiters-requests',
        '/super-admin/allsubscriptions',
        '/super-admin/analytics',
        '/super-admin/custom-pricing',
        '/super-admin/scheduled-demos',
        '/allsupporttickets',
        '/recruiter-applications',
        '/job-interview-history',
        '/admin/technologies',
        '/admin/offer-letters',
        '/admin/applications/:applicationId/release-offer',
        '/interview-panel',
        '/my-applications',
        '/my-interview-history',
        '/saved-jobs',
        '/analytics',
        '/analytics/candidate-comparison',
        '/direct-interviews',
        '/recruiter/interview-recordings',
        '/communication-center',
        '/communication-center/ai-calling',
        '/communication-center/voice-templates',
        '/communication-center/history',
        '/communication-center/ledger',
        '/communication-center/usage',
        '/communication-center/purchases',
    ];

    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppLayout sidebarRoutes={sidebarRoutes} />
        </Router>
    );
}

function AppLayout({ sidebarRoutes }) {
    const { isAuthenticated, user } = useAuth();
    const location = useLocation();

    const getDashboardPath = (u) => {
        if (!u) return '/';
        if (u.role === 'super_admin') return '/super-admin/dashboard';
        if (u.role === 'admin') return '/admin/dashboard';
        return '/candidate/dashboard';
    };

    useEffect(() => {
        // 1. Handle scroll to top or hash scrolling
        if (location.hash) {
            const id = location.hash.slice(1);
            const element = document.getElementById(id);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        } else {
            window.scrollTo(0, 0);
        }

        // 2. 🔥 Comprehensive fix for "stuck" scrolling during SPA navigation.
        // This resets body/html styles that might have been locked by modals,
        // drawers, or aborted transitions from the previous route.
        const forceScrollability = () => {
            try {
                // Explicitly clear scroll-lock styles from both body and document element
                const targets = [document.body, document.documentElement];
                targets.forEach(el => {
                    if (el) {
                        el.style.overflow = '';
                        el.style.overflowY = '';
                        el.style.height = '';
                        el.style.paddingRight = ''; // MUI adds this to compensate for scrollbar width
                        el.style.pointerEvents = 'auto'; // Ensure interactions are enabled
                    }
                });

                // Also check for common classes added by UI libraries (like MUI or Bootstrap)
                document.body.classList.remove('no-scroll', 'modal-open', 'mui-fixed');

                // Trigger a global resize event to force layout re-calculation for components
                // like sticky sidebars or virtualized lists.
                window.dispatchEvent(new Event('resize'));
            } catch (e) {
                console.error("Scroll restoration error:", e);
            }
        };

        // Initial cleanup
        forceScrollability();

        // Chain of timeouts to ensure we catch components that unmount late or async
        const timers = [
            setTimeout(forceScrollability, 50),
            setTimeout(forceScrollability, 150),
            setTimeout(forceScrollability, 400),
            setTimeout(forceScrollability, 800),
        ];

        return () => timers.forEach(t => clearTimeout(t));
    }, [location.pathname, location.hash]);

    const isInterviewPage = location.pathname.includes("/candidate/ai-interview/") ||
        location.pathname.includes("/candidate/ai-technical-interview/") ||
        location.pathname.includes("/candidate/ai-hr-interview/") ||
        location.pathname.includes("/candidate/direct-interview/");
    const [mobileOpen, setMobileOpen] = React.useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const showSidebar = isAuthenticated && (
        sidebarRoutes.some(path => location.pathname === path) ||
        location.pathname.startsWith('/candidate/') ||
        location.pathname.startsWith('/admin/') ||
        location.pathname.startsWith('/super-admin/')
    );


    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {!isInterviewPage && <Navbar />}
            <Box sx={{ display: 'flex', flexGrow: 1, zIndex: 1 }}>
                {!isInterviewPage && showSidebar && (
                    <Sidebar mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />
                )}

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        width: '100%',
                        overflowX: 'hidden',
                        pt: isInterviewPage ? 0 : { xs: '56px', md: '64px' },
                        margin: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '100vh',
                        position: 'relative'
                    }}
                >
                    {showSidebar && (
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{
                                display: { md: 'none' },
                                position: 'fixed',
                                bottom: 16,
                                right: 16,
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' },
                                zIndex: 10,
                                boxShadow: 3
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Box
                        key={location.pathname}
                        sx={{
                            flexGrow: 1,
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            maxWidth: { xxl: '1800px', xxxl: '2200px' },
                            mx: 'auto'
                        }}
                    >
                        <Routes>
                            {/* Public Routes */}
                            <Route
                                path="/"
                                element={<Landing />}
                            />
                            <Route path="/about" element={<About />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/book-demo" element={<BookDemo />} />
                            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                            <Route path="/user-pricing" element={<PrivateRoute adminOnly><UserPricing /></PrivateRoute>} />
                            <Route path="/pricing" element={<PrivateRoute adminOnly><Pricing /></PrivateRoute>} />
                            <Route path="/free-trial-confirm" element={<PrivateRoute adminOnly><FreeTrialConfirm /></PrivateRoute>} />
                            <Route
                                path="/login"
                                element={
                                    isAuthenticated ? (
                                        <Navigate to={getDashboardPath(user)} replace />
                                    ) : (
                                        <Login />
                                    )
                                }
                            />

                            <Route
                                path="/recruiter-login"
                                element={
                                    isAuthenticated ? (
                                        <Navigate to={getDashboardPath(user)} replace />
                                    ) : (
                                        <Login />
                                    )
                                }
                            />
                            <Route
                                path="/register"
                                element={
                                    isAuthenticated ? (
                                        <Navigate to={getDashboardPath(user)} replace />
                                    ) : (
                                        <Register />
                                    )
                                }
                            />
                            <Route path="/verify-email-otp" element={<VerifyEmailOtp />} />

                            <Route
                                path="/forgot-password"
                                element={
                                    isAuthenticated ? (
                                        <Navigate to={getDashboardPath(user)} replace />
                                    ) : (
                                        <ForgotPassword />
                                    )
                                }
                            />
                            <Route
                                path="/reset-password"
                                element={
                                    isAuthenticated ? (
                                        <Navigate to={getDashboardPath(user)} replace />
                                    ) : (
                                        <ResetPassword />
                                    )
                                }
                            />
                            <Route path="/jobs" element={<Jobs />} />
                            <Route path="/jobs/:id" element={<JobDetails />} />
                            {/* Public external job board application entry point */}
                            <Route path="/apply/:slug" element={<PublicJobPage />} />
                            <Route path="/interview-status/:publicToken" element={<InterviewStatus />} />

                            {/* Authenticated Routes */}
                            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                            <Route path="/subscription" element={<PrivateRoute adminOnly><Subscription /></PrivateRoute>} />
                            <Route path="/payment" element={<PrivateRoute adminOnly><Payment /></PrivateRoute>} />
                            <Route path="/support" element={<PrivateRoute><Support /></PrivateRoute>} />
                            <Route path="/payment-success" element={<PrivateRoute><PaymentSuccess /></PrivateRoute>} />
                            <Route path="/interview-history" element={<PrivateRoute><InterviewHistory /></PrivateRoute>} />
                            <Route path="/admin-request" element={<PrivateRoute><AdminRequest /></PrivateRoute>} />

                            {/* Candidate Routes */}
                            <Route path="/candidate/dashboard" element={<PrivateRoute><CandidateDashboard /></PrivateRoute>} />
                            <Route path="/candidate/coding-test/:applicationId" element={<PrivateRoute><CodingTest /></PrivateRoute>} />
                            <Route path="/candidate/ai-interview/:interviewId" element={<PrivateRoute><SystemCheckGate><AIInterview /></SystemCheckGate></PrivateRoute>} />
                            {/* AI Technical Interview — separate session with UUID token, isolated from assessment */}
                            <Route path="/candidate/ai-technical-interview/:sessionToken" element={<PrivateRoute><SystemCheckGate><AIInterview /></SystemCheckGate></PrivateRoute>} />
                            {/* AI HR Interview — independent session with its own UUID token */}
                            <Route path="/candidate/ai-hr-interview/:sessionToken" element={<PrivateRoute><SystemCheckGate><AIInterview /></SystemCheckGate></PrivateRoute>} />
                            <Route path="/candidate/technical-interview/:applicationId" element={<PrivateRoute><TechnicalInterview /></PrivateRoute>} />
                            <Route path="/candidate/hr-interview/:applicationId" element={<PrivateRoute><HRInterview /></PrivateRoute>} />

                            {/* Super Admin Routes */}
                            <Route path="/super-admin/dashboard" element={<PrivateRoute adminOnly><SuperAdminDashboard /></PrivateRoute>} />
                            <Route path="/super-admin/payments-history" element={<PrivateRoute adminOnly><Payment /></PrivateRoute>} />
                            <Route path="/super-admin/admin-history" element={<PrivateRoute adminOnly><SuperAdminAdmins /></PrivateRoute>} />
                            <Route path="/super-admin/user-history" element={<PrivateRoute adminOnly><SuperAdminUsers /></PrivateRoute>} />
                            <Route path="/super-admin/interview-history" element={<PrivateRoute adminOnly><SuperAdminInterviews /></PrivateRoute>} />
                            <Route path="/super-admin/jobs-history" element={<PrivateRoute adminOnly><SuperAdminJobs /></PrivateRoute>} />
                            <Route path="/super-admin/recruiters-requests" element={<PrivateRoute adminOnly><RecruiterRequests /></PrivateRoute>} />
                            <Route path="/super-admin/allsubscriptions" element={<PrivateRoute adminOnly><SuperAdminSubscriptions /></PrivateRoute>} />
                            <Route path="/super-admin/analytics" element={<PrivateRoute adminOnly><SuperAdminAnalytics /></PrivateRoute>} />
                            <Route path="/super-admin/custom-pricing" element={<PrivateRoute adminOnly><CustomPricingDashboard /></PrivateRoute>} />
                            <Route
                                path="/super-admin/scheduled-demos"
                                element={
                                    <PrivateRoute adminOnly>
                                        <ScheduledDemos />
                                    </PrivateRoute>
                                }
                            />
                            <Route path="/allsupporttickets" element={<PrivateRoute adminOnly><SuperAdminTickets /></PrivateRoute>} />

                            {/* Shared Protected Routes */}
                            <Route path="/recruiter-applications" element={<PrivateRoute adminOnly><JobApplications /></PrivateRoute>} />
                            <Route path="/job-interview-history" element={<PrivateRoute adminOnly><JobInterviewHistory /></PrivateRoute>} />
                            <Route path="/admin/technologies" element={<PrivateRoute adminOnly><Technologies /></PrivateRoute>} />
                            <Route path="/admin/offer-letters" element={<PrivateRoute adminOnly><OfferLetterTemplates /></PrivateRoute>} />
                            <Route path="/admin/applications/:applicationId/release-offer" element={<PrivateRoute adminOnly><ReleaseOfferLetter /></PrivateRoute>} />
                            <Route path="/interview-panel" element={<PrivateRoute adminOnly><InterviewPanel /></PrivateRoute>} />
                            <Route path="/my-applications" element={<PrivateRoute><MyApplications /></PrivateRoute>} />
                            <Route path="/my-interview-history" element={<PrivateRoute><InterviewHistory /></PrivateRoute>} />
                            <Route path="/saved-jobs" element={<PrivateRoute><SavedJobs /></PrivateRoute>}/>
                            <Route path="/admin/dashboard" element={<PrivateRoute adminOnly><AdminDashboard /></PrivateRoute>} />
                            <Route path="/admin/jobs" element={<PrivateRoute adminOnly><MyJobs /></PrivateRoute>} />

                            {/* Analytics Routes */}
                            <Route path="/analytics" element={<PrivateRoute adminOnly><Analytics /></PrivateRoute>} />
                            <Route path="/analytics/candidate-comparison" element={<PrivateRoute adminOnly><CandidateComparison /></PrivateRoute>} />

                            {/* Direct Interviews — standalone module */}
                            <Route path="/direct-interviews" element={<PrivateRoute adminOnly><DirectInterviews /></PrivateRoute>} />
                            {/* Direct Interview candidate entry point — reuses AIInterview engine transparently */}
                            <Route path="/candidate/direct-interview/:interviewId" element={<PrivateRoute><SystemCheckGate><AIInterview /></SystemCheckGate></PrivateRoute>} />

                            {/* Interview Recordings — isolated module, zero impact on existing interview flow */}
                            <Route path="/recruiter/interview-recordings" element={<PrivateRoute adminOnly><InterviewRecordings /></PrivateRoute>} />

                            {/* Communication Center Routes */}
                            <Route path="/communication-center" element={<PrivateRoute adminOnly><CommunicationOverview /></PrivateRoute>} />
                            <Route path="/communication-center/ai-calling" element={<PrivateRoute adminOnly><AICalling /></PrivateRoute>} />
                            <Route path="/communication-center/voice-templates" element={<PrivateRoute adminOnly><CommunicationTemplates type="voice" /></PrivateRoute>} />
                            <Route path="/communication-center/history" element={<PrivateRoute adminOnly><CommunicationHistory /></PrivateRoute>} />
                            <Route path="/communication-center/ledger" element={<PrivateRoute adminOnly><CreditLedger /></PrivateRoute>} />
                            <Route path="/communication-center/usage" element={<PrivateRoute adminOnly><CreditUsage /></PrivateRoute>} />
                            <Route path="/communication-center/purchases" element={<PrivateRoute adminOnly><PurchaseHistory /></PrivateRoute>} />

                            <Route path="/admin/jobs/new" element={<PrivateRoute adminOnly><PostJob /></PrivateRoute>} />
                            <Route path="/admin/jobs/:jobId/applications" element={<PrivateRoute adminOnly><JobApplications /></PrivateRoute>} />
                            <Route path="/admin/jobs/:jobId/candidates" element={<PrivateRoute adminOnly><CandidateScreening /></PrivateRoute>} />
                            <Route path="/admin/applications/:id" element={<PrivateRoute adminOnly><ApplicationDetails /></PrivateRoute>} />

                            {/* Fallback Route */}
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Box>
                </Box>
            </Box>
            {/* Footer moved outside the inner Box to ensure full width */}
            {!isInterviewPage && <Footer />}
        </Box>
    );
}

export default App;